import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { archives, categories, hotArchive } from "@/mocks/archive";
import { usePageSeo } from "@/hooks/usePageSeo";

export default function ArchiveDetail() {
  const { slug } = useParams<{ slug: string }>();
  const item = archives.find((a) => a.slug === slug);
  const cat = categories.find((c) => c.slug === item?.category);

  const related = item
    ? archives.filter((a) => a.category === item.category && a.slug !== item.slug).slice(0, 3)
    : [];
  const fill = item ? archives.filter((a) => a.slug !== item.slug).slice(0, 3) : [];

  usePageSeo(
    item ? `${item.name}（${item.role}）新面孔档案 - 新瓜档案` : "档案不存在 - 新瓜档案",
    item
      ? `最新吃瓜新晋人物 ${item.name}（${item.role}）档案：${item.summary}，新面孔瓜、新爆料主角一次认清。`
      : "抱歉，你访问的人物档案不存在。",
    item
      ? {
          id: "article-jsonld",
          data: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${item.name}（${item.role}）新面孔档案`,
            description: item.summary,
            image: item.imgs[0]?.src,
            datePublished: `${item.day}T09:00:00+08:00`,
            author: { "@type": "Organization", name: "新瓜档案" },
            publisher: { "@type": "Organization", name: "新瓜档案" },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://jxjyspt.cn/archives/${item.slug}`,
            },
          },
        }
      : null
  );

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-900">
        <Header />
        <main className="container-x flex flex-col items-center justify-center py-28 text-center">
          <i className="ri-file-search-line text-6xl text-foreground-300" />
          <h1 className="mt-6 font-heading text-3xl font-black text-foreground-950">该档案不存在或已下架</h1>
          <p className="mt-3 text-sm text-foreground-500">可能是链接有误，回到档案库看看其他新面孔吧。</p>
          <Link to="/archives" className="btn mt-8 bg-primary-500 text-background-50 hover:bg-primary-600">
            返回人物档案库 <i className="ri-arrow-right-line" />
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const hotList = hotArchive.filter((h) => h.slug !== item.slug).slice(0, 5);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Header />

      <main>
        {/* 面包屑 */}
        <section className="border-b border-background-200 bg-background-100/70">
          <div className="container-x py-4">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/archives" className="hover:text-primary-600">人物档案库</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to={`/archives?cat=${item.category}`} className="hover:text-primary-600">{cat?.name}</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-600">{item.name}</span>
            </nav>
          </div>
        </section>

        <section className="container-x py-10 md:py-14">
          <div className="grid gap-10 lg:grid-cols-12">
            {/* 主内容 */}
            <article className="lg:col-span-8">
              <header>
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/archives?cat=${item.category}`} className="tag bg-primary-100 text-primary-700 hover:bg-primary-200">
                    {cat?.name}
                  </Link>
                  <span className="tag">{item.tag}</span>
                </div>
                <h1 className="mt-4 font-heading text-3xl font-black leading-snug text-foreground-950 md:text-4xl">
                  {item.name} · {item.role}
                </h1>
                <p className="mt-3 border-l-4 border-accent-500 pl-4 font-heading text-lg font-semibold leading-relaxed text-foreground-600">
                  “{item.quote}”
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-400">
                  <span className="flex items-center gap-1.5"><i className="ri-calendar-line" /> 收录于 {item.day}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-eye-line" /> {item.views} 次浏览</span>
                  <span className="flex items-center gap-1.5"><i className="ri-time-line" /> 阅读约 {item.reading}</span>
                  <span className="flex items-center gap-1.5 text-primary-600"><i className="ri-fire-fill" /> 热度 {item.heat}</span>
                </div>
              </header>

              <figure className="mt-8 overflow-hidden rounded-lg">
                <img
                  src={item.imgs[0]?.src}
                  alt={item.imgs[0]?.alt || item.name}
                  className="aspect-[4/5] w-full object-cover object-top md:aspect-[16/10] md:object-cover md:object-top"
                />
              </figure>

              {/* 档案速览 */}
              <div className="mt-8 grid gap-3 rounded-lg bg-background-100 p-5 ring-1 ring-background-200 sm:grid-cols-2">
                {item.bullets.map((b) => (
                  <div key={b.k} className="flex items-center justify-between gap-3 rounded-md bg-background-50 px-4 py-3">
                    <span className="text-xs font-semibold text-foreground-400">{b.k}</span>
                    <span className="text-right text-sm font-semibold text-foreground-900">{b.v}</span>
                  </div>
                ))}
              </div>

              <div className="prose-custom mt-10 space-y-5">
                {item.detail.map((p, i) => (
                  <p key={i} className="text-[15px] leading-[1.95] text-foreground-700">
                    {i === 0 && <span className="float-left mr-2 font-heading text-5xl font-black leading-none text-primary-500">{item.name.charAt(0)}</span>}
                    {p}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="mr-1 text-xs text-foreground-400">相关标签：</span>
                <span className="tag">{item.category === "duan-shi" ? "短剧新秀" : item.category === "xuan-xiu" ? "选秀回锅" : item.category === "zhu-bo" ? "直播新锐" : item.category === "da-huo" ? "带货新势力" : "演艺新星"}</span>
                <span className="tag">新面孔瓜</span>
                <span className="tag">新兴人物</span>
              </div>

              {/* 阅读提示 */}
              <aside className="mt-8 flex gap-3 rounded-lg bg-secondary-100/70 p-4 text-sm text-secondary-900">
                <i className="ri-information-line mt-0.5 shrink-0 text-lg" />
                <p>
                  阅读提示：本站人物均为虚构示例，本档案用于演示「新晋人物 · 新爆料主角」的内容组织方式。请理性吃瓜，切勿以档案内容对任何人定性。
                </p>
              </aside>
            </article>

            {/* 侧栏 */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 lg:space-y-8">
                {/* 热度榜 */}
                <div className="rounded-lg bg-background-100 p-5 ring-1 ring-background-200">
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-fire-line text-accent-500" /> 本周热度榜
                  </h2>
                  <ol className="mt-4 space-y-3">
                    {hotList.map((h, i) => (
                      <li key={h.slug}>
                        <Link to={`/archives/${h.slug}`} className="group flex items-center gap-3">
                          <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black ${
                            i === 0 ? "bg-accent-500 text-background-50" : i === 1 ? "bg-secondary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}>{i + 1}</span>
                          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground-700 group-hover:text-primary-600">{h.name}</span>
                          <span className="shrink-0 text-xs font-semibold text-foreground-400">{h.heat}</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* 相关推荐 */}
                <div>
                  <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <i className="ri-archive-drawer-line text-primary-500" /> 更多新面孔
                  </h2>
                  <div className="mt-4 space-y-4">
                    {(related.length ? related : fill).map((r) => (
                      <Link key={r.slug} to={`/archives/${r.slug}`} className="group flex gap-3">
                        <img src={r.imgs[0]?.src} alt={r.imgs[0]?.alt || r.name} className="h-16 w-14 shrink-0 rounded-md object-cover object-top" />
                        <div className="min-w-0">
                          <p className="line-clamp-2 text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
                            {r.name} · {r.role}
                          </p>
                          <p className="mt-1 text-xs text-foreground-400">热度 {r.heat}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* 栏目导航 */}
                <div className="rounded-lg bg-background-100 p-5 ring-1 ring-background-200">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">浏览栏目</h2>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/archives?cat=${c.slug}`}
                        className={`tag ${c.slug === item.category ? "bg-primary-100 text-primary-700" : ""} hover:bg-primary-100`}
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}